// This script should be added as a new asset named 'urolithin-animation.js'
// It will find all Urolithin animation sections on the page and initialize them.

document.addEventListener('DOMContentLoaded', () => {
    const animationSections = document.querySelectorAll('.urolithin-animation-container');
    
    animationSections.forEach(container => {
        const canvas = container.querySelector('.urolithin-animation-canvas');
        if (canvas) {
            initializeUrolithinAnimation(container, canvas);
        }
    });
});

function initializeUrolithinAnimation(container, canvas) {
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // --- DOM Elements ---
    const titleEl = container.querySelector('.urolithin-title');
    const explanationEl = container.querySelector('.urolithin-explanation');
    const canvasWrapper = container.querySelector('.urolithin-canvas-wrapper');
    const playButtonOverlay = container.querySelector('.play-button-overlay');
    const audioEl = container.querySelector('.urolithin-audio');
    const audioToggleBtn = container.querySelector('.audio-toggle-button');

    // --- Animation & State Variables ---
    let isPaused = true;
    let isFirstPlaythrough = true;
    let W, H, scaleFactor;
    let cell;
    let particles = [];
    let urolithins = [];
    let lysosomes = [];
    
    let currentStage = 0;
    let stageTimer = 0;
    let lastTime = 0;
    
    // Durations timed to the provided audio track
    const stageDurations = [13000, 12000, 16000, 26400]; 

    let urolithinTargets = [];
    let nextUrolithinSpawnTime = 0;
    let urolithinSpawnInterval = 500;
    let mitophagyTargets = [];
    let nextMitophagyTime = 0;
    let mitophagyInterval = 1500;
    let restorationSpawns = [];
    let nextRestorationTime = 0;
    let restorationInterval = 1000;

    const stageInfo = [
        { title: "CELLULAR HEALTH & AGING", explanation: "Inside our cells, mitochondria produce energy. Over time, some become damaged, releasing harmful molecules and reducing cellular energy. This is a key factor in aging.", titleColor: "#93c5fd" },
        { title: "INTRODUCING UROLITHIN A", explanation: "But there’s something remarkable: Urolithin A. A natural compound produced when we digest certain foods—like raspberries—enters the cell and signals the start of a powerful cleanup.", titleColor: "#d8b4fe" },
        { title: "MITOPHAGY: CELLULAR RECYCLING", explanation: "Urolithin A triggers mitophagy—the selective removal of damaged mitochondria. An autophagosome forms around the damaged mitochondrion. A lysosome then fuses with it, breaking down and recycling the contents.", titleColor: "#86efac" },
        { title: "RESTORATION AND RENEWAL", explanation: "With the damaged mitochondria cleared, the cell functions more efficiently.\nThis cleanup sparks mitochondrial biogenesis—the creation of new, healthy mitochondria. Energy is restored, stamina returns, and vitality is renewed. Through this natural process, your cells regain balance, supporting long-term health and resilience.", titleColor: "#ef4444" }
    ];

    // --- UTILITY FUNCTIONS ---
    const lerp = (a, b, t) => a + (b - a) * t;
    const dist = (x1, y1, x2, y2) => Math.sqrt((x2 - x1)**2 + (y2 - y1)**2);
    const lerpColor = (c1, c2, amount) => {
        const f = (c) => parseInt(c.slice(1), 16);
        const t = (c) => c.toString(16).padStart(2, '0');
        const c1_val = f(c1), c2_val = f(c2);
        const r1 = c1_val >> 16, g1 = (c1_val >> 8) & 0xff, b1 = c1_val & 0xff;
        const r2 = c2_val >> 16, g2 = (c2_val >> 8) & 0xff, b2 = c2_val & 0xff;
        const r = Math.round(lerp(r1, r2, amount));
        const g = Math.round(lerp(g1, g2, amount));
        const b = Math.round(lerp(b1, b2, amount));
        return `#${t(r)}${t(g)}${t(b)}`;
    };
    
    // --- CLASSES ---
    class Particle {
        constructor() { this.x = Math.random() * W; this.y = Math.random() * H; this.vx = Math.random() * 0.4 - 0.2; this.vy = Math.random() * 0.4 - 0.2; this.radius = (Math.random() * 2.0 + 1.0) * scaleFactor; this.alpha = Math.random() * 0.6 + 0.2; }
        update(normalizedDelta = 1) { this.x += this.vx * normalizedDelta; this.y += this.vy * normalizedDelta; if (this.x < 0 || this.x > W) this.vx *= -1; if (this.y < 0 || this.y > H) this.vy *= -1; }
        draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2); ctx.fillStyle = `rgba(100, 150, 255, ${this.alpha})`; ctx.fill(); }
    }

    class Mitochondrion {
        constructor(x, y, damageLevel = 0) { this.x = x; this.y = y; this.vx = 0; this.vy = 0; this.angle = Math.random() * Math.PI * 2; this.angularVelocity = 0; this.radiusX = (30 + Math.random() * 10) * scaleFactor; this.radiusY = (15 + Math.random() * 5) * scaleFactor; this.damageLevel = damageLevel; this.wanderAngle = Math.random() * Math.PI * 2; this.state = 'normal'; this.fadeAlpha = 1; this.autophagosomeRadius = 0; this.autophagosomeComplete = false; this.lysosome = null; this.fragments = []; this.cristae = []; const numFolds = 5; const innerRadiusY = this.radiusY * 0.95; for (let i = 0; i < numFolds; i++) { this.cristae.push({ y: -innerRadiusY + (i + 1) * (innerRadiusY * 2 / (numFolds + 1)), yOffset: (Math.random() - 0.5) * 4 * scaleFactor, endYOffset: (Math.random() - 0.5) * 4 * scaleFactor, }); } }
        get isDamaged() { return this.damageLevel > 0.2; }
        update(allMitochondria, nucleus, cellRadius, cellCenter, normalizedDelta = 1) { allMitochondria.forEach(other => { if (this === other) return; const d = dist(this.x, this.y, other.x, other.y); const min_dist = (this.radiusX + other.radiusX) * 0.95; if (d < min_dist && d > 0) { const angle = Math.atan2(this.y - other.y, this.x - other.x); const force = (min_dist - d) * 0.01; this.vx += Math.cos(angle) * force * normalizedDelta; this.vy += Math.sin(angle) * force * normalizedDelta; } }); const d_nucleus = dist(this.x, this.y, nucleus.x, nucleus.y); if (d_nucleus < nucleus.radius + this.radiusX) { const angle = Math.atan2(this.y - nucleus.y, this.x - nucleus.x); const force = (nucleus.radius + this.radiusX - d_nucleus) * 0.02; this.vx += Math.cos(angle) * force * normalizedDelta; this.vy += Math.sin(angle) * force * normalizedDelta; } const d_cell_wall = dist(this.x, this.y, cellCenter.x, cellCenter.y); if (d_cell_wall > cellRadius - this.radiusX) { const angle = Math.atan2(cellCenter.y - this.y, cellCenter.x - this.x); const force = (d_cell_wall - (cellRadius - this.radiusX)) * 0.05; this.vx += Math.cos(angle) * force * normalizedDelta; this.vy += Math.sin(angle) * force * normalizedDelta; } this.wanderAngle += (Math.random() * 0.1 - 0.05) * normalizedDelta; this.vx += Math.cos(this.wanderAngle) * 0.005 * normalizedDelta; this.vy += Math.sin(this.wanderAngle) * 0.005 * normalizedDelta; this.vx *= Math.pow(0.92, normalizedDelta); this.vy *= Math.pow(0.92, normalizedDelta); this.angularVelocity *= Math.pow(0.95, normalizedDelta); this.x += this.vx * normalizedDelta; this.y += this.vy * normalizedDelta; this.angle += this.angularVelocity * normalizedDelta; if (this.state === 'fading') { this.fadeAlpha -= 0.005 * normalizedDelta; this.radiusX *= Math.pow(0.998, normalizedDelta); this.radiusY *= Math.pow(0.998, normalizedDelta); if (this.fadeAlpha <= 0) this.state = 'gone'; } if (this.state === 'growing') { this.radiusX = lerp(this.radiusX, 40 * scaleFactor, 1 - Math.pow(0.99, normalizedDelta)); this.radiusY = lerp(this.radiusY, 20 * scaleFactor, 1 - Math.pow(0.99, normalizedDelta)); } }
        draw() { if (this.state === 'gone') return; ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle); let baseAlpha = this.fadeAlpha; if (currentStage === 0 && this.isDamaged && !isPaused) { baseAlpha *= (0.7 + Math.sin(performance.now() * 0.01) * 0.3); } ctx.globalAlpha = baseAlpha; const outerStart = lerpColor("#d95f02", "#4d3319", this.damageLevel); const outerEnd = lerpColor("#fd8d3c", "#80552a", this.damageLevel); const outerGrad = ctx.createLinearGradient(-this.radiusX, 0, this.radiusX, 0); outerGrad.addColorStop(0, outerStart); outerGrad.addColorStop(1, outerEnd); ctx.fillStyle = outerGrad; ctx.shadowColor = lerpColor("#ff8c00", "#553311", this.damageLevel); ctx.shadowBlur = lerp(20, 5, this.damageLevel) * scaleFactor; ctx.beginPath(); ctx.ellipse(0, 0, this.radiusX, this.radiusY, 0, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0; const innerRadiusX = this.radiusX * 0.95; const innerRadiusY = this.radiusY * 0.95; ctx.save(); ctx.beginPath(); ctx.ellipse(0, 0, innerRadiusX, innerRadiusY, 0, 0, Math.PI * 2); ctx.clip(); const innerStart = lerpColor("#fed976", "#a67c52", this.damageLevel); const innerEnd = lerpColor("#feb24c", "#735639", this.damageLevel); const innerGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, innerRadiusX); innerGrad.addColorStop(0, innerStart); innerGrad.addColorStop(1, innerEnd); ctx.fillStyle = innerGrad; ctx.fill(); const growthFactor = this.state === 'growing' ? (this.radiusX - (5 * scaleFactor)) / (35 * scaleFactor) : 1; if (growthFactor > 0) { ctx.save(); ctx.globalAlpha = this.fadeAlpha * growthFactor; const cristaeR = lerp(217, 60, this.damageLevel); const cristaeG = lerp(95, 40, this.damageLevel); const cristaeB = lerp(2, 20, this.damageLevel); ctx.strokeStyle = `rgba(${cristaeR}, ${cristaeG}, ${cristaeB}, 0.6)`; ctx.lineWidth = 7 * scaleFactor * growthFactor; ctx.lineCap = 'round'; const foldsToDraw = Math.ceil(this.cristae.length * growthFactor); for (let i = 0; i < foldsToDraw; i++) { const fold = this.cristae[i]; const xStart = -innerRadiusX * 0.9; const xEnd = innerRadiusX * 0.9; ctx.beginPath(); ctx.moveTo(xStart, fold.y + fold.yOffset); const control1X = lerp(xStart, xEnd, 0.3); const control1Y = fold.y - this.radiusY * 0.8; const control2X = lerp(xStart, xEnd, 0.7); const control2Y = fold.y + this.radiusY * 0.8; ctx.bezierCurveTo(control1X, control1Y, control2X, control2Y, xEnd, fold.y + fold.endYOffset); ctx.stroke(); } ctx.restore(); } ctx.restore(); ctx.restore(); if (this.state === 'enveloped' || this.state === 'fading') { this.drawAutophagosome(); } }
        drawAutophagosome() { ctx.save(); ctx.translate(this.x, this.y); if (!this.autophagosomeComplete) { this.autophagosomeRadius = lerp(this.autophagosomeRadius, this.radiusX * 1.5, 0.02); if (this.autophagosomeRadius > this.radiusX * 1.45) { this.autophagosomeComplete = true; if (!this.lysosome) { this.lysosome = new Lysosome(this.x + 60 * scaleFactor, this.y - 60 * scaleFactor, this); lysosomes.push(this.lysosome); } } } const isFused = this.lysosome && this.lysosome.state === 'fused'; const color = isFused ? 'rgba(60, 220, 180, 0.4)' : 'rgba(0, 200, 255, 0.3)'; const shadow = isFused ? '#34d399' : '#00ccff'; ctx.strokeStyle = color; ctx.lineWidth = 3 * scaleFactor; ctx.shadowColor = shadow; ctx.shadowBlur = 15 * scaleFactor; ctx.beginPath(); ctx.arc(0, 0, this.autophagosomeRadius, 0, Math.PI * 2); ctx.stroke(); if (this.lysosome && this.lysosome.state !== 'gone') this.lysosome.draw(); ctx.restore(); }
        startMitophagy() { if (this.isDamaged) { this.state = 'enveloped'; } }
    }

    class UrolithinA {
        static path = new Path2D("M -14.5,15.6 L -23.5,15.6 L -29,6 L -23.5,-3.6 L -14.5,-3.6 L -9,6 L -14.5,15.6 Z M -9,6 L 0,6 L 5.5,-3.6 L 0,-13.2 L -9,-13.2 L -14.5,-3.6 L -9,6 Z M 5.5,-3.6 L 14.5,-3.6 L 20,6 L 14.5,15.6 L 5.5,15.6 L 0,6 L 5.5,-3.6 Z M -9,-13.2 L -9,-20.2 M -6,-20.2 L -12,-20.2 M -29,6 L -35,6 M 20,6 L 26,6");
        constructor(targetMito) { this.target = targetMito; this.scale = 0.8 * scaleFactor; this.spawnSide = Math.floor(Math.random() * 4); switch(this.spawnSide) { case 0: this.x = -this.scale * 20; this.y = Math.random() * H; break; case 1: this.x = W + this.scale * 20; this.y = Math.random() * H; break; case 2: this.x = Math.random() * W; this.y = -this.scale * 20; break; case 3: this.x = Math.random() * W; this.y = H + this.scale * 20; break; } this.state = 'approaching'; this.fadeAlpha = 1; this.angle = Math.random() * Math.PI * 2; this.hasEnteredCell = false; this.baseSpeed = 0.025 + (Math.random() - 0.5) * 0.01; }
        update(normalizedDelta = 1) { if (this.state === 'approaching' && this.target) { const targetX = this.target.x; const targetY = this.target.y; if (!this.hasEnteredCell) { if (dist(this.x, this.y, cell.x, cell.y) < cell.radius) { this.hasEnteredCell = true; } } const speedFactor = (this.hasEnteredCell ? this.baseSpeed * 0.6 : this.baseSpeed); this.x = lerp(this.x, targetX, 1 - Math.pow(1 - speedFactor, normalizedDelta)); this.y = lerp(this.y, targetY, 1 - Math.pow(1 - speedFactor, normalizedDelta)); this.angle = lerp(this.angle, this.target.angle, 1 - Math.pow(1 - speedFactor, normalizedDelta)); if (dist(this.x, this.y, targetX, targetY) < this.target.radiusX * 0.5) { this.state = 'attached'; } } else if (this.state === 'attached' && this.target) { this.x = this.target.x; this.y = this.target.y; this.angle = this.target.angle; } else if (this.state === 'fading') { this.fadeAlpha -= 0.02 * normalizedDelta; if (this.fadeAlpha <= 0) { this.state = 'gone'; } } }
        draw() { if (this.state === 'gone') return; ctx.save(); ctx.globalAlpha = this.fadeAlpha; if ((this.state === 'attached' || this.state === 'fading') && this.target) { const pulseAlpha = (this.state === 'fading') ? this.fadeAlpha : 1; ctx.globalAlpha = pulseAlpha * (0.3 + Math.sin(Date.now() * 0.005) * 0.3); ctx.beginPath(); const pulseRadius = this.target.radiusX * (1.1 + Math.sin(Date.now() * 0.005) * 0.2); ctx.arc(this.target.x, this.target.y, pulseRadius, 0, Math.PI * 2); ctx.strokeStyle = '#ff4444'; ctx.lineWidth = 2 * scaleFactor; ctx.shadowColor = '#ff4444'; ctx.shadowBlur = 15 * scaleFactor; ctx.stroke(); } ctx.globalAlpha = this.fadeAlpha; ctx.translate(this.x, this.y); ctx.rotate(this.angle); ctx.scale(this.scale, this.scale); ctx.strokeStyle = '#ff4444'; ctx.lineWidth = 4; ctx.shadowColor = '#ff0000'; ctx.shadowBlur = 10; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke(UrolithinA.path); ctx.restore(); }
    }
    
    class Lysosome {
        constructor(x, y, targetMito) { this.x = x; this.y = y; this.radius = 15 * scaleFactor; this.target = targetMito; this.state = 'approaching'; }
        update(normalizedDelta = 1) { const speed = 0.015; if (this.state === 'approaching') { this.x = lerp(this.x, this.target.x, 1 - Math.pow(1 - speed, normalizedDelta)); this.y = lerp(this.y, this.target.y, 1 - Math.pow(1 - speed, normalizedDelta)); if (dist(this.x, this.y, this.target.x, this.target.y) < this.target.autophagosomeRadius) { this.state = 'fusing'; } } else if (this.state === 'fusing') { this.radius = lerp(this.radius, 25 * scaleFactor, 1 - Math.pow(0.98, normalizedDelta)); if (this.radius > 24 * scaleFactor) { this.state = 'fused'; this.target.state = 'fading'; } } else if (this.state === 'fused') { if(this.target.state === 'gone') { this.state = 'gone'; } } }
        draw() { if (this.state === 'gone') return; const relX = this.x - this.target.x; const relY = this.y - this.target.y; ctx.save(); ctx.beginPath(); ctx.arc(relX, relY, this.radius, 0, Math.PI * 2); const grad = ctx.createRadialGradient(relX, relY, 1, relX, relY, this.radius); grad.addColorStop(0, 'rgba(150, 255, 200, 1)'); grad.addColorStop(1, 'rgba(50, 200, 100, 1)'); ctx.fillStyle = grad; ctx.shadowColor = '#34d399'; ctx.shadowBlur = 15 * scaleFactor; ctx.fill(); ctx.restore(); }
    }

    class Cell {
        constructor() { this.x = W / 2; this.y = H / 2; this.radius = Math.min(W, H) * 0.4; this.nucleus = { x: this.x, y: this.y, radius: this.radius * 0.3 }; this.mitochondria = []; this.pulseFactor = 1; this.pulseSpeed = 0.002;}
        initMitochondria() { this.mitochondria = []; const count = 22; for (let i = 0; i < count; i++) { const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.1; const r = this.nucleus.radius + 40 * scaleFactor + Math.random() * (this.radius - this.nucleus.radius - 80 * scaleFactor); const x = this.x + Math.cos(angle) * r; const y = this.y + Math.sin(angle) * r; let damageLevel = 0; if (Math.random() < 0.5) { damageLevel = 0.3 + Math.random() * 0.7; } this.mitochondria.push(new Mitochondrion(x, y, damageLevel)); } }
        draw() { const currentRadius = this.radius * this.pulseFactor; const currentNucleusRadius = this.nucleus.radius * this.pulseFactor; const grad = ctx.createRadialGradient(this.x, this.y, currentNucleusRadius, this.x, this.y, currentRadius); grad.addColorStop(0, 'rgba(50, 60, 120, 0.3)'); grad.addColorStop(1, 'rgba(20, 30, 80, 0.5)'); ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(this.x, this.y, currentRadius, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = 'rgba(100, 150, 255, 0.5)'; ctx.lineWidth = 10 * scaleFactor; ctx.shadowColor = 'rgba(100, 150, 255, 1)'; ctx.shadowBlur = 30 * scaleFactor; ctx.stroke(); ctx.shadowBlur = 0; const nucleusGrad = ctx.createRadialGradient(this.x, this.y, 1, this.x, this.y, currentNucleusRadius); nucleusGrad.addColorStop(0, 'rgba(150, 100, 255, 0.6)'); nucleusGrad.addColorStop(1, 'rgba(80, 50, 150, 0.7)'); ctx.fillStyle = nucleusGrad; ctx.beginPath(); ctx.arc(this.x, this.y, currentNucleusRadius, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = 'rgba(150, 100, 255, 0.8)'; ctx.lineWidth = 4 * scaleFactor; ctx.stroke(); }
        update(normalizedDelta = 1) { this.mitochondria.forEach(m => m.update(this.mitochondria, this.nucleus, this.radius, {x: this.x, y: this.y}, normalizedDelta)); }
        drawMitochondria() { this.mitochondria.forEach(m => m.draw()); }
    }
    
    // --- Core Animation & Sizing Logic ---
    function reinitializeAnimation() {
        const dpr = window.devicePixelRatio || 1;
        const canvasWrapper = canvas.parentElement;
        const cssWidth = canvasWrapper.clientWidth;
        const cssHeight = canvasWrapper.clientHeight;

        canvas.width = cssWidth * dpr;
        canvas.height = cssHeight * dpr;
        W = canvas.width;
        H = canvas.height;
        scaleFactor = Math.min(W, H) / 800;
        
        cell = new Cell();
        cell.initMitochondria();
        particles = [];
        for (let i = 0; i < 100; i++) particles.push(new Particle());
        
        reinitializeStageState(true);
    }
    
    function reinitializeStageState(fullReset = false) {
         if(fullReset) { urolithins = []; lysosomes = []; }
         const stageLogic = {
            1: () => { 
                urolithinTargets = cell.mitochondria.filter(m => m.isDamaged); 
                if (urolithinTargets.length > 0) {
                    const spawnDuration = 8000;
                    urolithinSpawnInterval = spawnDuration / urolithinTargets.length;
                }
                if(fullReset) nextUrolithinSpawnTime = 0; 
            },
            2: () => { 
                mitophagyTargets = cell.mitochondria.filter(m => m.isDamaged && m.state === 'normal'); 
                if (mitophagyTargets.length > 0) {
                    const mitophagyStartDuration = 12000; 
                    mitophagyInterval = mitophagyStartDuration / mitophagyTargets.length;
                } else {
                    mitophagyInterval = 1500;
                }
                if(fullReset) nextMitophagyTime = 500;
            },
        };
        if(stageLogic[currentStage]) stageLogic[currentStage]();
    }

    function advanceToStage(stage) {
        currentStage = stage;
        stageTimer = 0;
        const info = stageInfo[stage];
        canvas.classList.remove('pulsing-animation');
        void canvas.offsetWidth;
        canvas.classList.add('pulsing-animation');

        if (stage === 3 && cell && cell.mitochondria) {
            const damagedMitochondria = cell.mitochondria.filter(m => m.isDamaged);
            const damagedCount = damagedMitochondria.length;
            cell.mitochondria = cell.mitochondria.filter(m => !m.isDamaged);

            const numToRestore = damagedCount > 0 ? damagedCount + 7 : 0;
            restorationSpawns = [];
            if (numToRestore > 0) {
                const restorationStartDuration = 18000; 
                restorationInterval = restorationStartDuration / numToRestore;
                for (let i = 0; i < numToRestore; i++) {
                    const angle = Math.random() * Math.PI * 2;
                    const r = cell.nucleus.radius + Math.random() * (cell.radius - cell.nucleus.radius - 50);
                    restorationSpawns.push({x: cell.x + Math.cos(angle) * r, y: cell.y + Math.sin(angle) * r});
                }
            }
            nextRestorationTime = 1000;
        }

        const textContent = container.querySelector('.urolithin-text-content');
        textContent.style.opacity = 0;
        
        setTimeout(() => {
            titleEl.textContent = info.title;
            titleEl.style.color = info.titleColor;
            explanationEl.textContent = info.explanation;
            textContent.style.opacity = 1;
        }, 500);

        reinitializeStageState(true);
    }

    function playAnimation() {
        if (!isPaused) return;
        isPaused = false;
        if(audioEl && isFirstPlaythrough) {
            audioEl.play();
        }
        playButtonOverlay.classList.add('hidden');
        lastTime = performance.now();
        animationFrameId = requestAnimationFrame(animate);
    }

    function pauseAnimation() {
        if (isPaused) return;
        isPaused = true;
        if(audioEl) audioEl.pause();
        playButtonOverlay.classList.remove('hidden');
        cancelAnimationFrame(animationFrameId);
    }

    function togglePlayPause() {
        if (isPaused) {
            playAnimation();
        } else {
            pauseAnimation();
        }
    }

    function toggleAudio(event) {
        event.stopPropagation(); // Prevent the main canvas click from firing
        if (audioEl) {
            audioEl.muted = !audioEl.muted;
            if (audioEl.muted) {
                audioToggleBtn.classList.add('audio-muted');
            } else {
                audioToggleBtn.classList.remove('audio-muted');
            }
        }
    }

    function drawProgressIndicator() {
        if (isPaused) return;
        const progress = stageTimer / stageDurations[currentStage];
        const indicatorHeight = 2 * (window.devicePixelRatio || 1); // Thinner
        ctx.save();
        ctx.fillStyle = stageInfo[currentStage].titleColor;
        ctx.globalAlpha = 0.5; // More transparent
        ctx.fillRect(0, H - indicatorHeight, W * progress, indicatorHeight);
        ctx.restore();
    }

    function animate(timestamp) {
        if (isPaused) return;

        if (!lastTime) lastTime = timestamp;
        const deltaTime = timestamp - lastTime;
        lastTime = timestamp;
        const normalizedDelta = Math.min(2, deltaTime / (1000 / 60)); 

        stageTimer += deltaTime;
        
        let fadeAlpha = 0;
        const fadeDuration = 500;
        
        if (stageDurations[currentStage] - stageTimer < fadeDuration) {
            fadeAlpha = 1 - ((stageDurations[currentStage] - stageTimer) / fadeDuration);
        }
        else if (stageTimer < fadeDuration) {
            fadeAlpha = 1 - (stageTimer / fadeDuration);
        }

        if (stageTimer >= stageDurations[currentStage]) {
            const nextStage = (currentStage + 1) % stageDurations.length;
            if(nextStage === 0) { 
                reinitializeAnimation();
                isFirstPlaythrough = false; // It's no longer the first playthrough
                if(audioEl) {
                    audioEl.pause();
                    audioEl.currentTime = 0;
                }
            }
            advanceToStage(nextStage);
            animationFrameId = requestAnimationFrame(animate);
            return;
        }

        // --- Update logic ---
        if (currentStage === 3) {
            cell.pulseFactor = 1 + Math.sin(timestamp * cell.pulseSpeed) * 0.01;
        } else if (cell) {
            cell.pulseFactor = 1; 
        }

        if (currentStage === 1 && urolithinTargets.length > 0 && stageTimer > nextUrolithinSpawnTime) { 
            const t = urolithinTargets.shift(); 
            if(t) urolithins.push(new UrolithinA(t)); 
            nextUrolithinSpawnTime += urolithinSpawnInterval; 
        }
        if (currentStage === 2 && mitophagyTargets.length > 0 && stageTimer > nextMitophagyTime) { 
            const t=mitophagyTargets.shift(); 
            if(t){
                urolithins.forEach(u=>u.state="fading");
                t.startMitophagy();
            } 
            nextMitophagyTime += mitophagyInterval;
        }
        if (currentStage === 3 && restorationSpawns.length > 0 && stageTimer > nextRestorationTime) { 
            const t=restorationSpawns.shift(); 
            if(t){
                const e=new Mitochondrion(t.x,t.y,0);
                e.radiusX=5*scaleFactor;
                e.radiusY=2.5*scaleFactor;
                e.state="growing";
                cell.mitochondria.push(e)
            } 
            nextRestorationTime += restorationInterval; 
        }

        // --- Draw calls ---
        ctx.clearRect(0, 0, W, H);
        particles.forEach(p => { p.update(normalizedDelta); p.draw(); });
        cell.update(normalizedDelta);
        cell.draw();
        
        cell.mitochondria = cell.mitochondria.filter(m => m.state !== 'gone');
        cell.drawMitochondria();

        urolithins = urolithins.filter(u => u.state !== 'gone');
        urolithins.forEach(u => { u.update(normalizedDelta); u.draw(); });
        
        lysosomes = lysosomes.filter(l => l.state !== 'gone');
        lysosomes.forEach(l => { l.update(normalizedDelta); l.draw(); });

        drawProgressIndicator();

        if(fadeAlpha > 0) {
            ctx.fillStyle = `rgba(0, 0, 0, ${fadeAlpha})`;
            ctx.fillRect(0, 0, W, H);
        }
        
        animationFrameId = requestAnimationFrame(animate);
    }

    // --- Initial Setup ---
    function initialSetup() {
        reinitializeAnimation();
        advanceToStage(0);
        
        setTimeout(() => {
            ctx.clearRect(0, 0, W, H);
            particles.forEach(p => { p.update(); p.draw(); });
            cell.draw();
            cell.update();
            cell.drawMitochondria();
        }, 100);
    }
    
    let lastKnownWidth = window.innerWidth;
    window.addEventListener('resize', () => {
        // Only re-initialize if the width changes, to avoid mobile scroll issues
        if (window.innerWidth !== lastKnownWidth) {
            lastKnownWidth = window.innerWidth;
            if (isPaused) {
                initialSetup();
            } else {
                reinitializeAnimation();
            }
        }
    });

    canvasWrapper.addEventListener('click', togglePlayPause);
    if(audioToggleBtn) {
        audioToggleBtn.addEventListener('click', toggleAudio);
    }
    
    initialSetup();
}



